'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useRouter } from '@bprogress/next/app'
import { useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  EditIcon,
  TrashIcon,
  FileTextIcon,
  HashIcon,
  LanguagesIcon,
  FolderTreeIcon,
  ScanQrCodeIcon,
  CheckIcon,
  XIcon,
  PanelTopDashedIcon,
  PackageIcon,
  DollarSignIcon,
  CirclePileIcon,
  ReceiptCentIcon,
  Copy,
  DecimalsArrowRightIcon,
  PencilRulerIcon
} from 'lucide-react'
import Flag from 'react-flagkit'
import {
  formatDateTime,
  formatPriceGross,
  formatPriceNet,
  formatNumber,
  getCountryCodeFromLocale,
  getLocalizedValue,
  getTranslatedContentUrl
} from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import {
  CategoryPath,
  ContentDetailFiles,
  ContentDetailInfo,
  ContentDetailPictures,
  ContentError,
  DeleteConfirmationDialog,
  ProductKitsCard,
  TranslatedContentDetailLink
} from '@/components/partials'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableHeader, TableRow } from '@/components/ui/table'
import { useMetaStore } from '@/store'
import { useProduct, useDeleteProduct, useCopyToClipboard } from '@/hooks'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import Link from 'next/link'

type PropsType = {
  id: string
}

const ProductShowSkeleton = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-16" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <Skeleton className="h-9 w-20" />
    </div>
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-48" />
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Skeleton className="h-4 w-8" />
            <Skeleton className="h-6 w-32" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-6 w-24" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-36" />
          </div>
        </div>
      </CardContent>
    </Card>
  </div>
)

const Detail = ({ id }: PropsType) => {
  const t = useTranslations()
  const router = useRouter()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { copyToClipboard } = useCopyToClipboard()
  const [deleteProductId, setDeleteProductId] = useState<string | null>(null)

  const { data: product, isLoading, error } = useProduct(id)
  const catalogPrice = useMemo(() => {
    if (!product?.price) {
      return product?.price
    }
    return {
      ...product.price,
      gross: product.price.originalGross ?? product.price.gross,
      net: product.price.originalNet ?? product.price.net
    }
  }, [product])
  const { metaState } = useMetaStore()
  const defaultLocale = metaState.response?.generalSettings.adminDefaultLocale
  const routeParams = metaState.response?.routeParams
  // Wrap the metaState.response?.locales in its own useMemo to product unnecessary re-renders
  const localesData = useMemo(() => metaState.response?.locales, [metaState.response])
  const locales = useMemo(() => localesData || [], [localesData])
  // Find the locale object that matches the current locale
  const currentLocaleObj = locales.find((l: any) => l.fullCode === locale || l.code === locale)

  const { mutate: deleteProduct, isPending: isDeleting } = useDeleteProduct()

  const hasTranslation = (localeCode: string): boolean => {
    return product?.translations?.[localeCode] !== undefined
  }

  const handleDelete = () => {
    setDeleteProductId(id)
  }

  const confirmDeleteProduct = () => {
    if (deleteProductId) {
      deleteProduct(deleteProductId, {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: ['products'] })
          toast.success(t('deleted_successfully'))
          router.push('/products')
          setDeleteProductId(null)
        },
        onError: error => {
          console.error('Error deleting product:', error)
          toast.error(t('error_deleting'))
          setDeleteProductId(null)
        }
      })
    }
  }

  if (isLoading || isDeleting) {
    return <ProductShowSkeleton />
  }

  if (error || !product) {
    return <ContentError error={error} listUrl="/products" />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="grid grid-cols-1 items-center justify-between gap-2 md:grid-cols-2">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link href="/products">
              <ArrowLeftIcon className="h-4 w-4" />
              <span className="hidden lg:flex">{t('list')}</span>
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('product_details')}</h1>
            <p className="text-muted-foreground">{t('view_product_details')}</p>
          </div>
        </div>
        <div className="flex flex-row items-center justify-end gap-2">
          <Button variant="outline" asChild>
            <Link href={`/products/${id}/edit`}>
              <EditIcon className="h-4 w-4" />
              <span className="hidden items-center gap-2 lg:flex">{t('edit_product')}</span>
            </Link>
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <TrashIcon className="h-4 w-4" />
            <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
          </Button>
        </div>
      </div>

      {/* Product Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PackageIcon className="h-5 w-5" />
            {t('information')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-8">
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <HashIcon className="h-4 w-4" />
                ID
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-xs">
                  {product.id}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(product.id)}
                      className="h-6 w-6 p-0">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <ScanQrCodeIcon className="h-4 w-4" />
                {t('code')}
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="font-mono text-sm">
                  {product.code}
                </Badge>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(product.code, t('copied'))}
                      className="h-6 w-6 p-0">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t('copy')}</TooltipContent>
                </Tooltip>
              </div>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-4">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FileTextIcon className="h-4 w-4" />
                {t('title')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {getLocalizedValue(product, 'title', locale, defaultLocale)}
              </Badge>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-8">
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <DollarSignIcon className="h-4 w-4" />
                {t('entered_price')}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="font-mono text-sm">
                  {product.priceIncludesTax
                    ? formatPriceGross(catalogPrice, locale)
                    : formatPriceNet(catalogPrice, locale)}
                </Badge>
                <Badge variant={product.priceIncludesTax ? 'default' : 'secondary'} className="text-xs">
                  {product.priceIncludesTax ? t('price_with_tax') : t('price_without_tax')}
                </Badge>
              </div>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <DollarSignIcon className="h-4 w-4" />
                {t('price_without_tax')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {formatPriceNet(catalogPrice, locale)}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <DollarSignIcon className="h-4 w-4" />
                {t('price_with_tax')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {formatPriceGross(product.price, locale)}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <CirclePileIcon className="h-4 w-4" />
                {t('stock')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {formatNumber(product.stock, locale)}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <ReceiptCentIcon className="h-4 w-4" />
                {t('tax_rate')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.taxRate !== null && product.taxRate !== undefined
                  ? `%${formatNumber(product.taxRate, locale)}`
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <ReceiptCentIcon className="h-4 w-4" />
                {t('product_discount_rate')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.discountRate !== null && product.discountRate !== undefined
                  ? `%${formatNumber(product.discountRate, locale)}`
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <ReceiptCentIcon className="h-4 w-4" />
                {t('price_includes_tax')}
              </div>
              <div className="flex items-center gap-2">
                {product.priceIncludesTax ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">{t('yes')}</span>
                  </>
                ) : (
                  <>
                    <XIcon className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">{t('no')}</span>
                  </>
                )}
              </div>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PanelTopDashedIcon className="h-4 w-4" />
                {t('status')}
              </div>
              <div className="flex items-center gap-2">
                {product.active ? (
                  <>
                    <CheckIcon className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">{t('active')}</span>
                  </>
                ) : (
                  <>
                    <XIcon className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">{t('inactive')}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FolderTreeIcon className="h-4 w-4" />
                {t('category')}
              </div>
              <div className="flex items-center gap-2">
                <Badge key={product.category.id} variant="outline" className="font-mono text-sm">
                  <CategoryPath category={product.category} />
                </Badge>
              </div>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <FolderTreeIcon className="h-4 w-4" />
                {t('additional_categories')}
              </div>
              <div className="flex flex-wrap gap-2">
                {product.additionalCategories && product.additionalCategories.length > 0 ? (
                  <>
                    {product.additionalCategories.map(category => (
                      <Badge key={category.id} variant="outline" className="font-mono text-sm">
                        <CategoryPath category={category} />
                      </Badge>
                    ))}
                  </>
                ) : (
                  <>-</>
                )}
              </div>
            </div>
          </div>
          <Separator />
          <ContentDetailInfo entity={product} />
        </CardContent>
      </Card>

      {/* Measurements and Packaging */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PencilRulerIcon className="h-5 w-5" />
            {t('measurements_and_packaging')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-8">
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PackageIcon className="h-4 w-4" />
                {t('weight')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.weight !== undefined && product.weight !== null
                  ? `${formatNumber(product.weight, locale)} kg`
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PackageIcon className="h-4 w-4" />
                {t('units_per_box')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.unitsPerBox !== undefined && product.unitsPerBox !== null
                  ? formatNumber(product.unitsPerBox, locale)
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PackageIcon className="h-4 w-4" />
                {t('box_length')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.boxLength !== undefined && product.boxLength !== null
                  ? `${formatNumber(product.boxLength, locale)} cm`
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PackageIcon className="h-4 w-4" />
                {t('box_width')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.boxWidth !== undefined && product.boxWidth !== null
                  ? `${formatNumber(product.boxWidth, locale)} cm`
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PackageIcon className="h-4 w-4" />
                {t('box_height')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.boxHeight !== undefined && product.boxHeight !== null
                  ? `${formatNumber(product.boxHeight, locale)} cm`
                  : '-'}
              </Badge>
            </div>
            <div className="col-span-1 space-y-2 xl:col-span-2">
              <div className="text-muted-foreground flex items-center gap-2 text-sm font-medium">
                <PackageIcon className="h-4 w-4" />
                {t('box_dimensional_weight')}
              </div>
              <Badge variant="outline" className="font-mono text-sm">
                {product.boxDimensionalWeight !== undefined && product.boxDimensionalWeight !== null
                  ? formatNumber(product.boxDimensionalWeight, locale)
                  : '-'}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* OEM Numbers */}
      {product.oemNumbers && product.oemNumbers.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DecimalsArrowRightIcon className="h-5 w-5" />
              {t('oem_numbers')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="font-semibold">
                    <TableCell className="text-right">{t('sort_order')}</TableCell>
                    <TableCell>{t('vehicle_group')}</TableCell>
                    <TableCell>{t('brand')}</TableCell>
                    <TableCell>{t('model')}</TableCell>
                    <TableCell>{t('oem_number')}</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product.oemNumbers.map((oemNumber, index) => {
                    const vehicleGroup = oemNumber.vehicleGroup
                    let vehicleGroupTitle
                    if (vehicleGroup && currentLocaleObj && vehicleGroup.translations[currentLocaleObj.fullCode]) {
                      vehicleGroupTitle = vehicleGroup.translations[currentLocaleObj.fullCode].title
                    } else if (vehicleGroup) {
                      const firstTranslation = Object.values(vehicleGroup.translations)[0]
                      vehicleGroupTitle = firstTranslation?.title
                    } else {
                      vehicleGroupTitle = '-'
                    }

                    return (
                      <TableRow key={oemNumber.id || index}>
                        <TableCell className="w-12.5 text-right whitespace-nowrap">
                          {oemNumber.order || index + 1}
                        </TableCell>
                        <TableCell className="w-50 whitespace-nowrap">{vehicleGroupTitle || '-'}</TableCell>
                        <TableCell className="w-50 whitespace-nowrap">{oemNumber.model?.brand?.name || '-'}</TableCell>
                        <TableCell className="w-50 whitespace-nowrap">{oemNumber.model?.name || '-'}</TableCell>
                        <TableCell className="whitespace-nowrap">
                          <Badge variant="outline" className="font-mono">
                            {oemNumber.oem}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Translations */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <LanguagesIcon className="h-5 w-5" />
            {t('translations')}
          </CardTitle>
          <CardDescription>{t('available_translations')}</CardDescription>
        </CardHeader>
        <CardContent>
          {locales && locales.length > 0 ? (
            <Tabs defaultValue={locales[0].fullCode} className="w-full">
              <TabsList className="min-h-10.5 w-full">
                {locales.map(localeItem => (
                  <TabsTrigger
                    key={localeItem.fullCode}
                    value={localeItem.fullCode}
                    className="flex items-center gap-2"
                    disabled={!hasTranslation(localeItem.fullCode)}>
                    <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={16} />
                    <span className="hidden sm:inline">{localeItem.name}</span>
                  </TabsTrigger>
                ))}
              </TabsList>

              {locales.map(localeItem => {
                const translatedAppUrl = getTranslatedContentUrl(routeParams, 'product', localeItem.fullCode, product)

                return (
                  <TabsContent key={localeItem.fullCode} value={localeItem.fullCode} className="mt-6 space-y-4">
                    {hasTranslation(localeItem.fullCode) ? (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2">
                          <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                          <h3 className="text-lg font-semibold">{localeItem.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {localeItem.fullCode}
                          </Badge>
                        </div>

                        <Separator />

                        <div className="space-y-4">
                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('title')}</label>
                            <div className="bg-muted/50 rounded-lg border p-4">
                              <p className="text-sm leading-relaxed">
                                {product.translations[localeItem.fullCode].title}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('slug')}</label>
                            <div className="bg-muted/50 rounded-lg border p-4">
                              <p className="text-sm leading-relaxed">
                                {product.translations[localeItem.fullCode].slug}
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('content')}</label>
                            <div className="bg-muted/50 rounded-lg border p-4">
                              <div className="prose prose-sm dark:prose-invert html-content max-w-none">
                                {product.translations[localeItem.fullCode].content ? (
                                  <div
                                    dangerouslySetInnerHTML={{
                                      __html: product.translations[localeItem.fullCode].content || ''
                                    }}
                                  />
                                ) : (
                                  <p>-</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <label className="text-muted-foreground text-sm font-medium">{t('url')}</label>
                            <TranslatedContentDetailLink href={translatedAppUrl} />
                          </div>

                          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                              <label className="text-muted-foreground text-sm font-medium">{t('created_at')}</label>
                              <p className="text-sm">
                                {formatDateTime(product.translations[localeItem.fullCode].createdAt, locale)}
                              </p>
                            </div>

                            <div className="space-y-2">
                              <label className="text-muted-foreground text-sm font-medium">{t('updated_at')}</label>
                              <p className="text-sm">
                                {formatDateTime(product.translations[localeItem.fullCode].updatedAt, locale)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="py-8 text-center">
                        <div className="text-muted-foreground flex items-center justify-center gap-2">
                          <Flag country={getCountryCodeFromLocale(localeItem.fullCode)} size={20} />
                          <span>{localeItem.name}</span>
                        </div>
                        <p className="text-muted-foreground mt-2">{t('no_translation_available')}</p>
                      </div>
                    )}
                  </TabsContent>
                )
              })}
            </Tabs>
          ) : (
            <div className="py-8 text-center">
              <p className="text-muted-foreground">{t('no_languages_available')}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Properties */}
      {product.properties && product.properties.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">{t('product_properties')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {product.properties.map(property => {
                let propertyName: string
                if (property.translations[locale]) {
                  propertyName = property.translations[locale].name
                } else {
                  const firstTranslation = Object.values(property.translations)[0]
                  propertyName = firstTranslation?.name
                }
                return (
                  <div className="flex w-full flex-col items-start justify-items-center" key={property.id}>
                    <div className="flex w-full flex-col items-start justify-items-center space-y-2">
                      <div className="bg-muted/30 flex w-full items-center justify-between rounded-lg border px-3 py-2 font-bold">
                        {propertyName}
                      </div>
                      {property.fields && property.fields.length > 0 ? (
                        <div className="w-full rounded-md border">
                          <Table>
                            <TableBody>
                              {property.fields.map(field => {
                                let fieldName: string
                                if (field.translations[locale]) {
                                  fieldName = field.translations[locale].name
                                } else {
                                  const firstTranslation = Object.values(field.translations)[0]
                                  fieldName = firstTranslation?.name
                                }

                                return (
                                  <TableRow key={field.id}>
                                    <TableCell className="right w-37.5 align-top font-bold">{fieldName}</TableCell>
                                    <TableCell className="w-2.5 text-center align-top font-bold">:</TableCell>
                                    <TableCell>{field.value}</TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <div className="bg-muted/30 flex w-full items-center justify-between rounded-lg border px-3 py-2">
                          -
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pictures */}
      <ContentDetailPictures pictures={product.pictures} />

      {/* Files */}
      <ContentDetailFiles files={product.files} />

      {/* Repair Kits */}
      <ProductKitsCard products={product.repairKits} title={t('repair_kits')} emptyMessage={t('no_repair_kits')} />

      {/* Repaired Products */}
      <ProductKitsCard
        products={product.repairedProducts}
        title={t('repaired_kits')}
        emptyMessage={t('no_repaired_kits')}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={!!deleteProductId}
        onConfirm={confirmDeleteProduct}
        onCancel={() => setDeleteProductId(null)}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default Detail
